import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { BookOpen, Calendar, Check, FileText, Globe2, QrCode, Search, Share2, Tag } from "lucide-react";
import Navigation from "../components/Navigation";
import { Button } from "@/components/ui/button";
import { QrShareDialog } from "../components/QrShareDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CategoryPage({ user }) {
  const { name } = useParams();
  const category = decodeURIComponent(name || "");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [copied, setCopied] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const pageUrl = `${window.location.origin}/category/${encodeURIComponent(category)}`;

  useEffect(() => {
    let cancelled = false;
    const fetchItems = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ category, per_page: "100" });
        if (query.trim()) params.append("q", query.trim());
        const resp = await axios.get(`${API}/search?${params.toString()}`);
        if (!cancelled) {
          setItems(resp.data.items || []);
          setTotal(resp.data.total || 0);
        }
      } catch {
        if (!cancelled) {
          setItems([]);
          setTotal(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    const t = window.setTimeout(fetchItems, query ? 350 : 0);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [category, query]);

  const sharePage = async () => {
    const url = `${window.location.origin}/category/${encodeURIComponent(category)}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt("Copy this link:", url);
    }
  };

  return (
    <div className="min-h-screen bg-[#f7f6f2]">
      <Navigation user={user} />
      <main>
        <section className="border-b border-slate-200 bg-slate-950 text-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-orange-300 mb-4">
              <Tag className="w-4 h-4" /> The Archive · Category
            </div>
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <h1 className="text-4xl sm:text-5xl font-semibold" data-testid="category-page-title">{category}</h1>
                <p className="mt-3 text-slate-300" data-testid="category-page-count">
                  {loading ? "Loading…" : `${total} document${total === 1 ? "" : "s"} in this category`}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  onClick={sharePage}
                  className="bg-orange-600 hover:bg-orange-700 text-white"
                  data-testid="category-share-btn"
                >
                  {copied ? <Check className="w-4 h-4 mr-2" /> : <Share2 className="w-4 h-4 mr-2" />}
                  {copied ? "Link copied" : "Share this page"}
                </Button>
                <Button
                  type="button"
                  onClick={() => setShowQr(true)}
                  variant="outline"
                  className="border-slate-500 bg-transparent text-white hover:bg-white/10 hover:text-white"
                  data-testid="category-qr-btn"
                >
                  <QrCode className="w-4 h-4 mr-2" /> QR code
                </Button>
              </div>
            </div>
          </div>
        </section>

        <section className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
          <div className="relative mb-6 max-w-xl">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search within ${category}…`}
              className="w-full h-11 rounded-md border border-slate-200 bg-white pl-9 pr-3 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-400"
              data-testid="category-search-input"
            />
          </div>

          {!loading && items.length === 0 && (
            <div className="bg-white/70 border border-dashed border-slate-300 rounded-xl p-14 text-center" data-testid="category-empty-state">
              <BookOpen className="w-10 h-10 text-slate-300 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-slate-800">
                {query ? "No documents match your search" : "No documents in this category yet"}
              </h2>
              <p className="mt-2 text-sm text-slate-500">
                {query ? "Try different keywords." : "Check back soon — new material is added regularly."}
              </p>
            </div>
          )}

          <div className="space-y-4" data-testid="category-items-list">
            {items.map((item) => (
              <article key={item.id} className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 hover:border-orange-300 transition-colors">
                <Link to={`/items/${item.id}`} className="block" data-testid={`category-item-${item.id}`}>
                  <h3 className="text-lg font-semibold text-slate-950 hover:text-orange-700" style={{ fontFamily: "Fraunces, serif" }}>
                    {item.title}
                  </h3>
                </Link>
                {(item.authors || []).length > 0 && (
                  <p className="text-sm text-slate-500 mt-1">{item.authors.join(", ")}</p>
                )}
                {item.abstract && (
                  <p className="text-sm text-slate-600 leading-6 mt-2 line-clamp-2">{item.abstract}</p>
                )}
                <div className="flex flex-wrap items-center gap-3 mt-3 text-xs text-slate-500">
                  {item.year && (
                    <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{item.year}</span>
                  )}
                  {item.language && (
                    <span className="inline-flex items-center gap-1"><Globe2 className="w-3.5 h-3.5" />{item.language}</span>
                  )}
                  {item.file_type && (
                    <span className="inline-flex items-center gap-1"><FileText className="w-3.5 h-3.5" />{item.file_type}</span>
                  )}
                </div>
              </article>
            ))}
          </div>
        </section>

        <QrShareDialog
          open={showQr}
          onOpenChange={setShowQr}
          url={pageUrl}
          title={category}
          subtitle="The Archive · Scan to open this category"
        />
      </main>
    </div>
  );
}
